import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay, addDays, addMonths, addWeeks, startOfMonth, endOfMonth, endOfWeek } from "date-fns";
import { it } from "date-fns/locale";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./calendario.css";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarDays, ChevronDown, ChevronRight, Plus, SlidersHorizontal, X } from "lucide-react";
import { useSchermoLargo } from "@/lib/useSchermo";
import { NuovoEvento, SchedaEvento } from "./EventoDialog";
import { coloreEvento, dataPerVista, daVista, fmtEvento, oggiRoma, TIPI_EVENTO, evidenza, ordinaLista, scelteNuovo, titoloPeriodo } from "./calendarioComuni";

// Sprint CRM-2B — Giorno, Settimana e Mese con react-big-calendar; la Lista è scritta a mano
const localizer = dateFnsLocalizer({ format, parse, startOfWeek: (d) => startOfWeek(d, { weekStartsOn: 1 }), getDay, locales: { it } });
const FORMATI = { timeGutterFormat: "HH:mm", eventTimeRangeFormat: ({ start, end }, c, l) => `${l.format(start, "HH:mm", c)}–${l.format(end, "HH:mm", c)}`,
  dayHeaderFormat: "EEEE d MMMM", dayFormat: "EEE d", weekdayFormat: "EEE" };
const VISTE = [["lista", "Lista"], ["day", "Giorno"], ["week", "Settimana"], ["month", "Mese"]];
const LS = "cal-scelti";
const LS_VISTA = "cal-vista";  // Sprint CRM-5 — ultima vista scelta
const LS_VISTA_TELEFONO = "cal-vista-telefono";  // Sprint CRM-6 — sotto 1024px: scelta a parte, prima apertura in Lista
const leggiVista = (k) => { try { return localStorage.getItem(k) || "lista"; } catch { return "lista"; } };

function useCalendari() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/calendario/calendari").then((r) => setC(r.data)).catch(() => setC({ mio: null, sedi: [], colleghi: [] })); }, []);
  return c;
}

function Scelte({ cal, scelti, setScelti, onChiudi }) {
  const [chiuse, setChiuse] = useState({});
  const voce = (k, l, tid = k) => (
    <label key={tid} className="flex items-center gap-2 text-sm py-0.5"><Checkbox checked={scelti.includes(k)} data-testid={`cal-scelta-${tid}`}
      onCheckedChange={(v) => setScelti(v ? [...scelti, k] : scelti.filter((x) => x !== k))} />{l}</label>
  );
  // Sprint CRM-5 — per AZIENDA, poi le sue sedi; una sede condivisa compare sotto ogni sua azienda (evento = azienda × sede)
  const aziende = [...new Map(cal.sedi.map((s) => [s.company_id, s.azienda_nome || "—"])).entries()].sort((a, b) => a[1].localeCompare(b[1]));
  return (
    <Card className={`p-3 shrink-0 space-y-2 ${onChiudi ? "fixed top-0 left-0 z-50 h-screen overflow-y-auto rounded-none w-72 max-w-[85vw]" : "w-60"}`} data-testid="cal-scelte">
      {onChiudi && <div className="flex items-center justify-between"><span className="font-semibold text-sm">Calendari</span>
        <button type="button" onClick={onChiudi} className="p-1 rounded hover:bg-slate-100" aria-label="Chiudi calendari" data-testid="cal-scelte-chiudi"><X className="h-4 w-4" /></button></div>}
      {voce("mio", "Il mio calendario")}
      {!!cal.sedi.length && <div className="text-xs uppercase text-slate-500 pt-2">Sedi</div>}
      {aziende.map(([cid, nome]) => (
        <div key={cid} data-testid={`cal-azienda-${cid}`}>
          <button type="button" className="flex items-center gap-1 text-sm font-medium text-slate-700 w-full text-left" data-testid={`cal-azienda-apri-${cid}`}
            onClick={() => setChiuse({ ...chiuse, [cid]: !chiuse[cid] })}>
            {chiuse[cid] ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}{nome}</button>
          {!chiuse[cid] && <div className="pl-5">{cal.sedi.filter((s) => s.company_id === cid).map((s) => voce(`sede:${s.site_id}`, s.nome, `sede:${s.site_id}:${cid}`))}</div>}
        </div>
      ))}
      {!!cal.colleghi.length && <div className="text-xs uppercase text-slate-500 pt-2">Colleghi</div>}
      {cal.colleghi.map((u) => voce(`utente:${u.id}`, u.nome))}
    </Card>
  );
}

function Lista({ eventi, onApri }) {
  const oggi = oggiRoma();
  return (
    <div className="space-y-1" data-testid="cal-lista">
      {!eventi.length && <div className="text-sm text-slate-400 p-4">Nessun evento</div>}
      {ordinaLista(eventi, oggi).map((ev) => (
        <button key={ev.id} type="button" onClick={() => onApri(ev)} data-testid={`cal-lista-${ev.id}`}
          className={`w-full text-left flex items-center gap-3 rounded border px-3 py-2 text-sm transition-colors hover:bg-slate-50 ${evidenza(ev, oggi) ? "border-amber-300 bg-amber-50" : "border-slate-200"} ${ev.fatto ? "opacity-60" : ""}`}>
          <span className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: coloreEvento(ev) }} />
          <span className="font-mono w-28 sm:w-36 shrink-0 text-xs sm:text-sm">{fmtEvento(ev)}</span>
          <span className="flex-1 min-w-0"><span className="font-medium block truncate">{ev.titolo}</span>
            {ev.descrizione && <span className="block truncate text-xs text-slate-500" title={ev.descrizione} data-testid={`cal-lista-descrizione-${ev.id}`}>{ev.descrizione}</span>}</span>
          <span className="text-xs text-slate-500">{TIPI_EVENTO[ev.tipo]}{ev.fatto ? " · fatto" : ""}</span>
        </button>
      ))}
    </div>
  );
}

const intervallo = (vista, d) => (vista === "month" ? [startOfWeek(startOfMonth(d), { weekStartsOn: 1 }), addDays(endOfWeek(endOfMonth(d), { weekStartsOn: 1 }), 1)]
  : vista === "week" ? [startOfWeek(d, { weekStartsOn: 1 }), addDays(endOfWeek(d, { weekStartsOn: 1 }), 1)] : [d, addDays(d, 1)]);

export default function Calendario() {
  const { user } = useAuth() || {};
  const cal = useCalendari();
  const [sp] = useSearchParams();
  const [scelti, setSceltiState] = useState(() => { try { return JSON.parse(localStorage.getItem(LS)) || ["mio"]; } catch { return ["mio"]; } });
  const setScelti = (s) => { setSceltiState(s); try { localStorage.setItem(LS, JSON.stringify(s)); } catch {} };
  const largo = useSchermoLargo();
  const chiaveVista = largo ? LS_VISTA : LS_VISTA_TELEFONO;
  const [vista, setVistaState] = useState(() => leggiVista(chiaveVista));
  useEffect(() => { setVistaState(leggiVista(chiaveVista)); }, [chiaveVista]);
  const setVista = (v) => { setVistaState(v); try { localStorage.setItem(chiaveVista, v); } catch {} };
  const [scelteAperte, setScelteAperte] = useState(false);
  const [data, setData] = useState(() => { const g = oggiRoma().split("-"); return new Date(+g[0], +g[1] - 1, +g[2]); });
  const [fatti, setFatti] = useState(false);
  const [eventi, setEventi] = useState([]);
  const [nuovo, setNuovo] = useState(null);
  const [aperto, setAperto] = useState(null);
  const carica = useCallback(() => {
    const p = new URLSearchParams({ calendari: scelti.join(","), fatti: vista === "lista" ? String(fatti) : "true" });
    if (vista !== "lista") { const [a, b] = intervallo(vista, data); p.set("dal", daVista(a)); p.set("al", daVista(b)); }
    api.get(`/calendario/eventi?${p}`).then((r) => setEventi(r.data)).catch(() => setEventi([]));
  }, [scelti, vista, data, fatti]);
  useEffect(() => { carica(); }, [carica]);
  useEffect(() => { const id = sp.get("evento"); if (id) api.get(`/calendario/eventi/${id}`).then((r) => setAperto(r.data)).catch(() => {}); }, [sp]);
  const perVista = useMemo(() => eventi.map((ev) => ({ ev, title: ev.titolo, allDay: !!ev.tutto_il_giorno, start: dataPerVista(ev.inizio),
    end: ev.fine ? dataPerVista(ev.fine) : new Date(dataPerVista(ev.inizio).getTime() + 30 * 60000) })), [eventi]);
  const sposta = (dir) => setData((d) => (vista === "month" ? addMonths(d, dir) : vista === "week" ? addWeeks(d, dir) : addDays(d, dir)));
  const fatto = () => { setNuovo(null); setAperto(null); carica(); };
  if (!cal) return null;
  return (
    <div className="space-y-4" data-testid="calendario-page">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight flex items-center gap-2">
          <CalendarDays className="h-6 w-6 text-blue-600" /> Calendario</h1>
        <div className="flex gap-1 lg:ml-4">
          <Button size="sm" variant="outline" onClick={() => { const g = oggiRoma().split("-"); setData(new Date(+g[0], +g[1] - 1, +g[2])); }} data-testid="cal-oggi">Oggi</Button>
          <Button size="sm" variant="outline" onClick={() => sposta(-1)} disabled={vista === "lista"} data-testid="cal-indietro">Indietro</Button>
          <Button size="sm" variant="outline" onClick={() => sposta(1)} disabled={vista === "lista"} data-testid="cal-avanti">Avanti</Button>
        </div>
        {!largo && <Button size="sm" variant="outline" onClick={() => setScelteAperte(true)} data-testid="cal-scelte-apri">
          <SlidersHorizontal className="h-4 w-4 mr-1" /> Calendari ({scelti.length})</Button>}
        <div className="flex gap-1 flex-wrap">
          {VISTE.map(([k, l]) => <Button key={k} size="sm" variant={vista === k ? "default" : "outline"} onClick={() => setVista(k)} data-testid={`cal-vista-${k}`}>{l}</Button>)}
        </div>
        <span className="font-heading text-lg font-semibold text-slate-800" data-testid="cal-periodo">{titoloPeriodo(vista, data)}</span>
        {vista === "lista" && <label className="flex items-center gap-2 text-sm"><Checkbox checked={fatti} onCheckedChange={(v) => setFatti(!!v)} data-testid="cal-mostra-fatti" /> Mostra fatti</label>}
        <Button className="ml-auto bg-[#0F4C81] hover:bg-[#0c3d68]" onClick={() => setNuovo("")} data-testid="cal-nuovo-btn"><Plus className="h-4 w-4 mr-1" /> Nuovo evento</Button>
      </div>
      <div className="flex gap-4 items-start">
        {largo ? <Scelte cal={cal} scelti={scelti} setScelti={setScelti} /> : scelteAperte && <>
          <div className="fixed inset-0 z-40 bg-slate-900/50" onClick={() => setScelteAperte(false)} data-testid="cal-scelte-sfondo" />
          <Scelte cal={cal} scelti={scelti} setScelti={setScelti} onChiudi={() => setScelteAperte(false)} /></>}
        <Card className="p-3 flex-1 min-w-0">
          {vista === "lista" ? <Lista eventi={eventi} onApri={setAperto} /> : (
            <div className="cal-gestionale h-[680px]" data-testid="cal-vista">
              <Calendar localizer={localizer} culture="it" events={perVista} view={vista} date={data} onView={() => {}} onNavigate={setData}
                toolbar={false} formats={FORMATI} onSelectEvent={(e) => setAperto(e.ev)} selectable onSelectSlot={(s) => setNuovo(daVista(s.start))}
                eventPropGetter={(e) => ({ style: { backgroundColor: coloreEvento(e.ev), opacity: e.ev.fatto ? 0.55 : 1 } })} />
            </div>
          )}
        </Card>
      </div>
      {nuovo !== null && <NuovoEvento scelte={scelteNuovo(cal, user)} iniziale={nuovo} onChiudi={() => setNuovo(null)} onFatto={fatto} />}
      {aperto && <SchedaEvento ev={aperto} onChiudi={() => setAperto(null)} onFatto={fatto} />}
    </div>
  );
}
